import { FC, useEffect, useState } from 'react'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import cn from './index.module.css'
import Modal from '@common/Modal'
import classNames from 'classnames'
import { IcoRemove } from '@/utils/icons'
import TextField from '@pages/Admin/CourseSettings/components/TextField/TextField'
import { useSelector } from 'react-redux'
import { selectProfile } from '@store/profile.reducer'
import PrimaryButton from '@pages/Admin/CourseSettings/components/PrimaryButton/PrimaryButton'
import { sendEmailCode, verifyEmailCode } from './utils/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { checkIsEmail } from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/utils/validation'
import SecondaryButton from '@pages/Admin/CourseSettings/components/SecondaryButton/SecondaryButton'
import { usersAPI } from '@/api'

interface IRequiredEmailConfirmationModal {
  open: boolean
  onSuccess?: () => void
  onDismiss?: () => void
}

type VerificationStep = 'send-code' | 'confirm-code'

const ModalBody: FC<IRequiredEmailConfirmationModal> = (props) => {
  const [step, setStep] = useState<VerificationStep>('send-code')

  const goTo = (next: VerificationStep) => {
    setStep(next)
  }

  const onSendCodeDone = () => goTo('confirm-code')

  const onSuccess = () => {
    props?.onSuccess?.()
  }

  return (
    <div className={mclasses.modalContainer}>
      <div className={mclasses.modalHeader}>
        <div className={mclasses.modalTitleContainer}>
          <span className={mclasses.modalHeaderTitle}>
            Подтвердите свой email
          </span>
        </div>
        {props?.onDismiss && (
          <div className={mclasses.closeIcon} onClick={props?.onDismiss}>
            <IcoRemove />
          </div>
        )}
      </div>
      {step === 'send-code' && <SendCodeStep onSuccess={onSendCodeDone} />}
      {step === 'confirm-code' && (
        <ConfirmCodeStep
          onSuccess={onSuccess}
          onBack={() => goTo('send-code')}
        />
      )}
    </div>
  )
}

const SendCodeStep = ({ onSuccess }: { onSuccess: () => void }) => {
  const addAlert = useAddAlert()
  const profile = useSelector(selectProfile)
  const [email, setEmail] = useState(profile?.email ?? '')
  const [isLoading, setLoading] = useState(false)

  useEffect(() => {
    setEmail(profile?.email ?? '')
  }, [profile?.email])

  const onSendEmail = async () => {
    if (isLoading) return

    setLoading(true)
    try {
      if (email !== profile.email) {
        try {
          const checkResult = await usersAPI.checkEmail(email)
          if (checkResult.data?.exists) {
            addAlert({
              title: 'Ошибка',
              text: 'Данная почта зарегистрирована на другого пользователя',
              type: 'error',
            })
            return
          }
        } catch (checkError) {
          console.warn('Не удалось проверить существование email:', checkError)
        }
      }

      const res = await sendEmailCode(profile.id, email)
      
      if (res.success) {
        addAlert({
          title: `Код отправлен на почту ${email}`,
          text: 'Введите код для подтверждения почты',
        })
        onSuccess()
      } else {
        const errorMessage = res.error || 'Неизвестная ошибка'
        addAlert({
          title: 'Ошибка',
          text: `Не удалось отправить код подтверждения. ${errorMessage}`,
          type: 'error',
        })
      }
    } catch (error) {
      console.error('Критическая ошибка при отправке кода:', error)
      addAlert({
        title: 'Критическая ошибка',
        text: 'Произошла критическая ошибка. Попробуйте ещё раз или обратитесь к администратору',
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className={mclasses.formWrapper}>
        <TextField
          label="Введите свою почту"
          description="На указанную почту придёт код с подтверждением"
          value={email}
          onChange={setEmail}
          disabled={isLoading}
        />
      </div>
      <div className={mclasses.actions}>
        <PrimaryButton
          onClick={onSendEmail}
          disabled={isLoading || !checkIsEmail(email)}
          isLoading={isLoading}
        >
          Отправить
        </PrimaryButton>
      </div>
    </>
  )
}

const ConfirmCodeStep = ({
  onBack,
  onSuccess,
}: {
  onBack: () => void
  onSuccess: () => void
}) => {
  const addAlert = useAddAlert()
  const profile = useSelector(selectProfile)
  const [code, setCode] = useState('')
  const [isLoading, setLoading] = useState(false)

  const onSendCode = async () => {
    if (isLoading) return
    
    setLoading(true)
    try {
      const res = await verifyEmailCode(profile.id, code)
      
      if (res.success) {
        addAlert({
          title: 'Успех',
          text: 'Почта была успешно подтверждена',
        })
        onSuccess()
      } else {
        const errorMessage = res.error || 'Неизвестная ошибка'
        addAlert({
          title: 'Ошибка',
          text: `Не удалось подтвердить код. ${errorMessage}`,
          type: 'error',
        })
      }
    } catch (error) {
      console.error('Критическая ошибка при подтверждении кода:', error)
      addAlert({
        title: 'Критическая ошибка',
        text: 'Произошла критическая ошибка. Попробуйте ещё раз или обратитесь к администратору',
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className={mclasses.formWrapper}>
        <TextField
          label={`Код`}
          description="Введите код из письма"
          value={code}
          onChange={setCode}
          disabled={isLoading}
        />
      </div>
      <div className={mclasses.actions}>
        <SecondaryButton onClick={onBack}>Назад</SecondaryButton>
        <PrimaryButton
          onClick={onSendCode}
          disabled={isLoading || !code}
          isLoading={isLoading}
        >
          Подтвердить
        </PrimaryButton>
      </div>
    </>
  )
}

const RequiredEmailConfirmationModal: FC<IRequiredEmailConfirmationModal> = (
  props,
) => {
  const { open, onDismiss } = props

  return (
    <>
      <Modal
        active={open}
        className={classNames(mclasses.modalWrapper, cn.modalWrapper) as any}
        setActive={onDismiss}
      >
        <ModalBody {...props} />
      </Modal>
    </>
  )
}

export default RequiredEmailConfirmationModal
